import * as html from 'smg/html';
import Modal from 'smg/modal';

// Real objects a customer can hold against the screen, in inches
const OBJECTS = {
    quarter: { widthIn: 0.955, heightIn: 0.955, round: true, instruction: 'Hold a US quarter against the screen and fit the circle until it covers the coin.' },
    card: { widthIn: 3.37, heightIn: 2.125, round: false, instruction: 'Press a credit card flat to the monitor and stretch the rectangle out to its edges.' },
};
type objectKey = keyof typeof OBJECTS;

// A card's corners are 1/8" radius
const CARD_CORNER_IN = 0.125;
// CSS px per real inch the outline can be set to — phones run ~150, desktops ~100
const MIN_PX_PER_INCH = 40;
const MAX_PX_PER_INCH = 260;
const STEP_PX_PER_INCH = 0.5;
// Browsers lay out at 96 CSS px per "inch"; the starting guess before a first match
const DEFAULT_PX_PER_INCH = 96;
// A real credit card is ~430 px on a typical laptop and wider on a tablet; only the large
// dialog (Bootstrap's modal-lg, which starts at 992 px) has room to match one
const CARD_MIN_VIEWPORT_PX = 992;
// The well's side padding (no-minimum-custom-pins.scss .size-check-stage): the outline
// is never allowed wider than the room inside it
const STAGE_PADDING_PX = 20;

/**
 * Asks the customer to match an on-screen outline to a real quarter or credit card.
 * Resolves the matched CSS px per real inch, or null when they cancel.
 */
export function OpenSizeCheck(currentPxPerInch: number | null): Promise<number | null> {
    return new Promise(resolve => {
        const allowCard = window.innerWidth >= CARD_MIN_VIEWPORT_PX;
        let pxPerInch = currentPxPerInch ?? DEFAULT_PX_PER_INCH;
        let object: objectKey = 'quarter';

        const $body = document.createElement('div');
        $body.className = 'd-flex flex-column gap-3';
        $body.append(html.FromString(`
            ${allowCard ? `
            <div class="btn-group" role="group" aria-label="What you're holding">
                <input type="radio" class="btn-check" name="size-check-object" id="size-check-quarter" value="quarter" checked>
                <label class="btn btn-outline-primary" for="size-check-quarter"><i class="fas fa-circle me-1"></i>US quarter</label>
                <input type="radio" class="btn-check" name="size-check-object" id="size-check-card" value="card">
                <label class="btn btn-outline-primary" for="size-check-card"><i class="fas fa-credit-card me-1"></i>Credit card</label>
            </div>` : ''}
            <p class="mb-0" id="size-check-instruction"></p>
            <div class="size-check-stage bg-body-tertiary rounded-2 d-flex align-items-center justify-content-center overflow-hidden">
                <div class="size-check-outline border border-2 border-primary bg-primary-subtle d-flex align-items-center justify-content-center" id="size-check-outline">
                    <span class="small fw-bold text-primary text-nowrap" id="size-check-outline-label" aria-hidden="true"></span>
                </div>
            </div>
            <div class="d-flex align-items-center gap-2">
                <label for="size-check-range" class="fw-semibold text-nowrap">Resize outline</label>
                <button type="button" class="btn btn-outline-secondary" id="size-check-shrink" aria-label="Make the outline smaller"><i class="fas fa-minus"></i></button>
                <input type="range" class="form-range flex-grow-1" id="size-check-range" min="${MIN_PX_PER_INCH}" max="${MAX_PX_PER_INCH}" step="${STEP_PX_PER_INCH}">
                <button type="button" class="btn btn-outline-secondary" id="size-check-grow" aria-label="Make the outline bigger"><i class="fas fa-plus"></i></button>
            </div>
        `));

        const $stage = $body.querySelector<HTMLElement>('.size-check-stage');
        const $instruction = $body.querySelector('#size-check-instruction');
        const $outline = $body.querySelector<HTMLElement>('#size-check-outline');
        const $outlineLabel = $body.querySelector('#size-check-outline-label');
        const $range = $body.querySelector<HTMLInputElement>('#size-check-range');

        // The largest outline that still fits the well for the object being matched —
        // a card is 3.5× a quarter's width, so its ceiling is much lower
        const maxPxPerInch = (): number => {
            const room = ($stage?.clientWidth ?? 0) - STAGE_PADDING_PX * 2;
            if (room <= 0) {
                return MAX_PX_PER_INCH;
            }
            const fits = Math.floor(room / OBJECTS[object].widthIn / STEP_PX_PER_INCH) * STEP_PX_PER_INCH;
            return Math.max(MIN_PX_PER_INCH, Math.min(MAX_PX_PER_INCH, fits));
        };

        const render = (): void => {
            const shape = OBJECTS[object];
            if ($instruction) {
                $instruction.textContent = shape.instruction;
            }
            if ($outlineLabel) {
                $outlineLabel.textContent = `${shape.widthIn}"`;
            }
            if ($outline) {
                $outline.style.width = `${shape.widthIn * pxPerInch}px`;
                $outline.style.height = `${shape.heightIn * pxPerInch}px`;
                if (shape.round) {
                    $outline.style.borderRadius = '50%';
                } else {
                    $outline.style.borderRadius = `${CARD_CORNER_IN * pxPerInch}px`;
                }
            }
            if ($range) {
                $range.max = String(maxPxPerInch());
                $range.value = String(pxPerInch);
            }
        };

        const set = (value: number): void => {
            const stepped = Math.round(value / STEP_PX_PER_INCH) * STEP_PX_PER_INCH;
            pxPerInch = Math.max(MIN_PX_PER_INCH, Math.min(maxPxPerInch(), stepped));
            render();
        };

        $body.querySelectorAll<HTMLInputElement>('input[name="size-check-object"]').forEach($radio => {
            $radio.addEventListener('change', () => {
                if ($radio.checked && ($radio.value === 'quarter' || $radio.value === 'card')) {
                    object = $radio.value;
                    // Re-clamp: an outline sized for a quarter can be too big as a card
                    set(pxPerInch);
                }
            });
        });
        $range?.addEventListener('input', () => set(parseFloat($range.value)));
        $body.querySelector('#size-check-shrink')?.addEventListener('click', () => set(pxPerInch - STEP_PX_PER_INCH));
        $body.querySelector('#size-check-grow')?.addEventListener('click', () => set(pxPerInch + STEP_PX_PER_INCH));

        // Drag the outline sideways to grow or shrink it, like stretching it over the object
        let drag: { startX: number; startPxPerInch: number } | null = null;
        $outline?.addEventListener('pointerdown', e => {
            drag = { startX: e.clientX, startPxPerInch: pxPerInch };
            $outline.setPointerCapture(e.pointerId);
        });
        $outline?.addEventListener('pointermove', e => {
            if (drag) {
                set(drag.startPxPerInch + (e.clientX - drag.startX) * 2 / OBJECTS[object].widthIn);
            }
        });
        // Capture ends with the drag however it ends: a lift, a cancel, or the browser taking the touch
        $outline?.addEventListener('lostpointercapture', () => {
            drag = null;
        });

        render();

        // The well's width is only known once the dialog is laid out, and changes with the window
        const refit = (): void => set(pxPerInch);
        window.addEventListener('resize', refit);

        let saved = false;
        new Modal({
            title: 'Line up an object',
            body: $body,
            // Wide enough for a real credit card on a typical laptop
            size: allowCard ? 'modal-lg' : undefined,
            confirmText: 'Save this fit',
            cancelText: 'Cancel',
            onSubmit: () => {
                saved = true;
                resolve(pxPerInch);
            },
            onClose: () => {
                window.removeEventListener('resize', refit);
                if (!saved) {
                    resolve(null);
                }
            },
        });
        requestAnimationFrame(refit);
    });
}
